import { FullScreenLayout } from '@layouts/FullScreenLayout';
import { lazy } from 'react';
import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import { Flags, useFlag } from 'ui';
import { ProtectedRoute } from '@components/ProtectedRoute';
import { FullScreenLayoutProvider } from '@context/FullScreenLayoutContext';
import { CaseDashboardProvider } from '@pages/CaseDashboard/CaseDashboardContext';
import { NotFound } from '@pages/NotFound';
import { UploadClaimsFileProvider } from '@pages/UploadClaimsFile/UploadClaimsFileContext';
import { UsersManagementProvider } from '@pages/UsersManagement/UsersManagementContext';
import { PATHS } from '@utils/paths';
import { PrivateLayout } from '../../../layouts/PrivateLayout';
import { buildCaseSettingsRouteDefinitions } from './caseSettingsRoutesDefinition';
import { CaseSettingsRootRedirect } from './redirect-auxiliaries';

const Login = lazy(() => import('../../../pages/Login'));
const ForgotPassword = lazy(() => import('../../../pages/ForgotPassword'));
const CaseDashboard = lazy(() => import('../../../pages/CaseDashboard'));
const ClaimForm = lazy(() => import('../../../pages/ClaimForm'));
const UsersManagement = lazy(() => import('../../../pages/UsersManagement'));
const Security = lazy(() => import('../../../pages/Security'));
const AccountActivation = lazy(
  () => import('../../../pages/AccountActivation'),
);
const AuthManagement = lazy(() => import('../../../pages/AuthManagement'));
const ResetPassword = lazy(() => import('../../../pages/ResetPassword'));
const ScheduledReports = lazy(() => import('../../../pages/ScheduledReports'));
const CaseSettings = lazy(() => import('../../../pages/CaseSettings'));
const NewCaseCreation = lazy(() => import('../../../pages/NewCaseCreation'));
const UploadClaimsFile = lazy(() => import('../../../pages/UploadClaimsFile'));
const ClaimFiles = lazy(() => import('../../../pages/ClaimFiles'));
const InvalidLinkInvitation = lazy(
  () => import('../../../pages/InvalidLinkInvitation'),
);

const DeveloperTools = lazy(() => import('../../../pages/DeveloperTools'));
const ClaimscoreSettings = lazy(
  () => import('../../../pages/ClaimscoreSettings'),
);

export const useAppRoutes = () => {
  const isCaseSettingsActive = useFlag(Flags.caseSettings);
  const isUploadClaimsFileEnabled = useFlag(Flags.uploadClaimsFile);
  const isClaimscoreSettingsEnabled = useFlag(Flags.claimscoreSettings);

  const caseSettingsPageElement = (
    <ProtectedRoute hasAccess={isCaseSettingsActive}>
      <CaseSettings />
    </ProtectedRoute>
  );

  const caseSettingsRoutes: RouteObject[] = buildCaseSettingsRouteDefinitions(
    Boolean(isClaimscoreSettingsEnabled),
  ).map((def) => ({
    path: def.path,
    element:
      def.kind === 'caseSettingsPage' ? (
        caseSettingsPageElement
      ) : (
        <CaseSettingsRootRedirect />
      ),
  }));

  const claimscoreSettingsRoutes: RouteObject[] = isClaimscoreSettingsEnabled
    ? [
        {
          path: PATHS.PRIVATE_BASE.claimscoreSettings,
          element: (
            <ProtectedRoute hasAccess={Boolean(isClaimscoreSettingsEnabled)}>
              <ClaimscoreSettings />
            </ProtectedRoute>
          ),
        },
      ]
    : [];

  const publicRoutes: RouteObject[] = [
    { path: PATHS.PUBLIC.login, element: <Login /> },
    { path: PATHS.PUBLIC.forgotPassword, element: <ForgotPassword /> },
    { path: PATHS.PUBLIC.auth, element: <AuthManagement /> },
    { path: PATHS.PUBLIC.passwordReset, element: <ResetPassword /> },
    {
      path: PATHS.PUBLIC.accountActivation,
      element: <AccountActivation />,
    },
    {
      path: PATHS.PUBLIC.pageNotFound,
      element: <NotFound />,
    },
    {
      path: PATHS.PUBLIC.invalidLinkInvitation,
      element: <InvalidLinkInvitation />,
    },
    {
      path: '*',
      element: <Navigate to="/404" />,
    },
  ];

  const privateClaimFormRoute: RouteObject[] = [
    {
      path: PATHS.PRIVATE_BASE.root,
      element: <PrivateLayout />,
      children: [{ path: '/claim-form', element: <ClaimForm /> }],
    },
  ];

  const noLayoutPrivateRoutes: RouteObject[] = [
    {
      path: PATHS.PRIVATE_BASE.newCaseCreation,
      element: (
        <ProtectedRoute hasAccess={Boolean(isCaseSettingsActive)}>
          <FullScreenLayoutProvider>
            <FullScreenLayout>
              <NewCaseCreation />
            </FullScreenLayout>
          </FullScreenLayoutProvider>
        </ProtectedRoute>
      ),
    },
    {
      path: PATHS.PRIVATE_BASE.uploadClaimsFile,
      element: (
        <ProtectedRoute hasAccess={Boolean(isUploadClaimsFileEnabled)}>
          <FullScreenLayoutProvider>
            <FullScreenLayout>
              <UploadClaimsFileProvider>
                <UploadClaimsFile />
              </UploadClaimsFileProvider>
            </FullScreenLayout>
          </FullScreenLayoutProvider>
        </ProtectedRoute>
      ),
    },
  ];

  const privateCaseLayoutRoutes: RouteObject[] = [
    {
      path: PATHS.PRIVATE_BASE.root,
      element: <PrivateLayout withNavbar withSidebar />,
      children: [
        {
          path: '',
          element: <Navigate to={PATHS.PRIVATE_BASE.dashboard} />,
        },
        {
          index: true,
          path: PATHS.PRIVATE_BASE.dashboard,
          element: (
            <CaseDashboardProvider>
              <CaseDashboard />
            </CaseDashboardProvider>
          ),
        },
        {
          path: PATHS.PRIVATE_BASE.claimFiles,
          element: (
            <ProtectedRoute hasAccess={Boolean(isUploadClaimsFileEnabled)}>
              <ClaimFiles />
            </ProtectedRoute>
          ),
        },
        {
          path: PATHS.PRIVATE_BASE.permissions,
          element: (
            <UsersManagementProvider>
              <UsersManagement />
            </UsersManagementProvider>
          ),
        },
        {
          path: PATHS.PRIVATE_BASE.security,
          element: <Security />,
        },
        {
          path: PATHS.PRIVATE_BASE.scheduledReports,
          element: <ScheduledReports />,
        },
        ...claimscoreSettingsRoutes,
        ...caseSettingsRoutes,
        {
          path: PATHS.PRIVATE_BASE.developerTools,
          element: <DeveloperTools />,
        },
      ],
    },
  ];

  const routes: RouteObject[] = [
    ...publicRoutes,
    ...privateCaseLayoutRoutes,
    ...privateClaimFormRoute,
    ...noLayoutPrivateRoutes,
  ];

  const router = createBrowserRouter(routes);

  return { routes, router };
};

export default useAppRoutes;
